import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getPublicLinkByContainer, getCompanyYardInfo } from "./api";

export default function ContainerLookup() {
  const navigate = useNavigate();
  const { companyId, yardId } = useParams();
  const [containerNo, setContainerNo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [orgInfo, setOrgInfo] = useState(null);
  const ASSET_LABELS = {
    Container: {
      hero: "Container Inspection",
      inputLabel: "CONTAINER NUMBER",
      description:
        "Enter your container number below to view the inspection report.",
      validationMsg:
        "Container number must be 4 letters followed by 7 digits (e.g. MSCU1234567)",
      invalidMsg:
        "Invalid format. Must be 4 letters followed by 7 digits (e.g. MSCU1234567)",
      emptyMsg: "Please enter a container number",
    },
    IsoTank: {
      hero: "Tank Inspection",
      inputLabel: "TANK NUMBER",
      description:
        "Enter your tank number below to view the inspection report.",
      validationMsg:
        "Tank number must be 4 letters followed by 7 digits (e.g. MSCU1234567)",
      invalidMsg:
        "Invalid format. Must be 4 letters followed by 7 digits (e.g. MSCU1234567)",
      emptyMsg: "Please enter a tank number",
    },
    Other: {
      hero: "Asset Inspection",
      inputLabel: "ASSET NUMBER",
      description:
        "Enter your asset number below to view the inspection report.",
      validationMsg: "Asset number must be 4 letters followed by 7 digits",
      invalidMsg: "Invalid format. Must be 4 letters followed by 7 digits",
      emptyMsg: "Please enter an asset number",
    },
  };
  const assetType = orgInfo?.asset_types?.[0] || "Container";
  const labels = ASSET_LABELS[assetType] || ASSET_LABELS["Container"];

  useEffect(() => {
    if (!companyId) return;
    getCompanyYardInfo(companyId, yardId)
      .then((res) => setOrgInfo(res.data))
      .catch(() => {});
  }, [companyId, yardId]);

  const handleContainerInput = (e) => {
    const value = e.target.value.toUpperCase();
    setError("");
    if (value.length > 11) return;
    if (value.length === 0) {
      setContainerNo("");
      return;
    }
    if (value.length <= 4) {
      if (/^[A-Z]*$/.test(value)) setContainerNo(value);
      return;
    }
    const letters = value.slice(0, 4);
    const numbers = value.slice(4);
    if (/^[A-Z]{4}$/.test(letters) && /^[0-9]*$/.test(numbers))
      setContainerNo(value);
  };

  const handleSubmit = async () => {
    const trimmed = containerNo.trim();
    if (!trimmed) {
      setError(labels.emptyMsg); 
      return;
    }
    if (trimmed.length < 11) {
      setError(labels.validationMsg);
      return;
    }
    if (!/^[A-Z]{4}[0-9]{7}$/.test(trimmed)) {
      setError(labels.invalidMsg); 
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await getPublicLinkByContainer(trimmed, companyId, yardId);
      const publicLink = res.data;
      const scanStageId = publicLink.split("/public-report-details/")[1];
      if (scanStageId) {
        navigate(`/public-report-details/${scanStageId}`);
      } else {
        window.location.href = publicLink;
      }
    } catch (err) {
      setError(
        err?.response?.data?.message || "No report found for this container.",
      );
    } finally {
      setLoading(false);
    }
  };

  const getFormatHint = () => {
    if (containerNo.length === 0) return null;
    if (containerNo.length < 4)
      return {
        text: `${4 - containerNo.length} more letter(s) needed`,
        color: "#f59e0b",
      };
    if (containerNo.length === 4)
      return { text: "Now enter 7 digits", color: "#3b82f6" };
    if (containerNo.length < 11)
      return {
        text: `${11 - containerNo.length} more digit(s) needed`,
        color: "#f59e0b",
      };
    if (containerNo.length === 11)
      return { text: "✓ Valid format", color: "#10b981" };
    return null;
  };

  const hint = getFormatHint();

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#f1f5f9",
        fontFamily: "'Inter', sans-serif",
        padding: "20px",
        gap: "0",
      }}
    >
      {/* ── Welcome Hero Banner ── */}
      <div
        style={{
          width: "100%",
          maxWidth: 440,
          background: "linear-gradient(135deg, #3182ce, #2b6cb0)",
          borderRadius: "20px 20px 0 0",
          padding: "28px 32px 24px",
          color: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* subtle background pattern */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0.06,
            backgroundImage:
              "radial-gradient(circle at 20% 50%, #fff 1px, transparent 1px), radial-gradient(circle at 80% 20%, #fff 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />

        {/* Powered by */}
        <div
          style={{
            position: "absolute",
            top: 16,
            right: 20,
            fontSize: "10px",
            color: "rgba(255,255,255,0.45)",
            fontWeight: 500,
            letterSpacing: "0.5px",
          }}
        >
          Powered by Visotonics
        </div>

        {/* Company initials avatar */}
        <div
          style={{
            width: "48px",
            height: "48px",
            borderRadius: "14px",
            background: "rgba(255,255,255,0.15)",
            border: "1.5px solid rgba(255,255,255,0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "16px",
            fontWeight: 800,
            color: "#fff",
            letterSpacing: "1px",
            marginBottom: "14px",
          }}
        >
          {"🚚"}
        </div>

        {orgInfo ? (
          <>
            <div
              style={{
                fontSize: "11px",
                color: "rgba(255,255,255,0.55)",
                fontWeight: 600,
                letterSpacing: "1px",
                textTransform: "uppercase",
                marginBottom: "4px",
              }}
            >
              Welcome to
            </div>
            {/* Yard name big */}
            {yardId && orgInfo.yard_name ? (
              <>
                <div
                  style={{
                    fontSize: "22px",
                    fontWeight: 800,
                    color: "#fff",
                    lineHeight: 1.2,
                    marginBottom: "4px",
                  }}
                >
                  {orgInfo.company_name}
                </div>
              </>
            ) : (
              <div
                style={{
                  fontSize: "22px",
                  fontWeight: 800,
                  color: "#fff",
                  lineHeight: 1.2,
                }}
              >
                {orgInfo.company_name}
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: "20px", fontWeight: 800, color: "#fff" }}>
            {labels.hero}
          </div>
        )}
      </div>

      {/* ── Main Card ── */}
      <div
        style={{
          background: "#ffffff",
          borderRadius: "0 0 20px 20px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.10)",
          padding: "32px 32px 28px",
          width: "100%",
          maxWidth: 440,
          borderTop: "1px solid #e2e8f0",
        }}
      >
        <p
          style={{
            fontSize: "14px",
            color: "#64748b",
            margin: "0 0 24px",
            lineHeight: 1.6,
          }}
        >
          {labels.description}
        </p>

        {/* Input */}
        <div style={{ marginBottom: hint ? 6 : 20 }}>
          <label
            style={{
              display: "block",
              fontSize: "12px",
              fontWeight: 600,
              color: "#374151",
              marginBottom: "6px",
              letterSpacing: "0.3px",
            }}
          >
           {labels.inputLabel}
            <span
              style={{
                fontWeight: 400,
                color: "#94a3b8",
                marginLeft: 6,
                textTransform: "none",
                letterSpacing: 0,
              }}
            >
              (4 letters + 7 digits)
            </span>
          </label>
          <input
            type="text"
            value={containerNo}
            onChange={handleContainerInput}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            placeholder="e.g. MSCU1234567"
            autoFocus
            style={{
              width: "100%",
              border: `1.5px solid ${error ? "#ef4444" : containerNo.length === 11 ? "#10b981" : "#e2e8f0"}`,
              borderRadius: "10px",
              padding: "13px 16px",
              fontSize: "18px",
              fontWeight: 600,
              letterSpacing: "3px",
              textAlign: "center",
              color: "#1e293b",
              outline: "none",
              fontFamily: "'Courier New', monospace",
              boxSizing: "border-box",
              background: "#f8fafc",
              transition: "border-color 0.2s, background 0.2s",
            }}
            onFocus={(e) => {
              e.target.style.background = "#fff";
              if (!error && containerNo.length !== 11)
                e.target.style.borderColor = "#1d4ed8";
            }}
            onBlur={(e) => {
              e.target.style.background = "#f8fafc";
              if (!error && containerNo.length !== 11)
                e.target.style.borderColor = "#e2e8f0";
            }}
          />
        </div>

        {hint && !error && (
          <p
            style={{
              fontSize: "12px",
              color: hint.color,
              margin: "4px 0 14px",
              textAlign: "center",
              fontWeight: 500,
            }}
          >
            {hint.text}
          </p>
        )}

        {error && (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "8px",
              padding: "10px 14px",
              marginBottom: "16px",
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
            }}
          >
            <span style={{ fontSize: "14px", flexShrink: 0 }}>⚠️</span>
            <p style={{ fontSize: "13px", color: "#dc2626", margin: 0 }}>
              {error}
            </p>
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{
            width: "100%",
            background: loading
              ? "#93c5fd"
              : "linear-gradient(135deg, #3182ce, #2b6cb0)",
            color: "#ffffff",
            border: "none",
            borderRadius: "10px",
            padding: "14px",
            fontSize: "15px",
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            letterSpacing: "0.3px",
            boxShadow: loading ? "none" : "0 4px 14px rgba(29,78,216,0.3)",
            transition: "opacity 0.2s",
          }}
          onMouseEnter={(e) => {
            if (!loading) e.currentTarget.style.opacity = "0.9";
          }}
          onMouseLeave={(e) => {
            if (!loading) e.currentTarget.style.opacity = "1";
          }}
        >
          {loading ? (
            <>
              <span
                style={{
                  width: 16,
                  height: 16,
                  border: "2px solid #fff",
                  borderTopColor: "transparent",
                  borderRadius: "50%",
                  display: "inline-block",
                  animation: "spin 0.7s linear infinite",
                }}
              />
              Looking up...
            </>
          ) : (
            "View Report →"
          )}
        </button>

        <p
          style={{
            fontSize: "12px",
            color: "#94a3b8",
            textAlign: "center",
            marginTop: "18px",
            marginBottom: 0,
          }}
        >
          🔒 Public page — no login required
        </p>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
